import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, UntypedFormControl } from '@angular/forms';
import { Observable, ReplaySubject, Subject, of } from 'rxjs';
import { share, switchMap, takeUntil, debounceTime, distinctUntilChanged, startWith, map, filter } from 'rxjs/operators';
import { Router } from '@angular/router';
import { MatSelectChange } from '@angular/material/select';

import * as ibantools from 'ibantools';

import { canonicalizeBusinessIdControl, clientBusinessIdValidator } from '../../validators/business-id/angular-adapter';
import { ConfirmationDialogModel } from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import { ConfirmationService } from 'src/app/shared/confirmation/confirmation.service';
import { toElectronicIban } from 'src/app/shared/iban/iban.util';
import { CountriesMap, CountriesMapCache } from 'src/app/model/model-map-cache';
import { Client, ClientBankAccount, ClientRepresentative, ClientType, CreateApplicationMetadata } from 'src/app/api/models';
import { ApplicationsService, ClientsService } from 'src/app/api/services';

// Ownership-confirmed account first, then newest first. The backend returns
// accounts ordered id ASC (oldest first), which is the wrong default
// for invoice entry.
export function compareBankAccounts(a: ClientBankAccount, b: ClientBankAccount): number {
  if (!!a.verified_at !== !!b.verified_at) {
    return a.verified_at ? -1 : 1;
  }
  return b.id - a.id;
}

@Component({
  selector: 'app-invoice-create-page',
  templateUrl: './invoice-create-page.component.html',
  styleUrls: ['./invoice-create-page.component.scss']
})
// This page is for creating invoices for CompanyClients. A new page will be needed for LightEntrepreneurs.
export class InvoiceCreatePageComponent implements OnInit, OnDestroy {
  private readonly ngDestroy$ = new Subject();

  countries$: Observable<CountriesMap>;

  form: UntypedFormGroup;
  uploadFiles: File[] = [];
  clientCtrl = new UntypedFormControl();
  clients$: Observable<Client[]>;
  clientRepresentatives$: Observable<ClientRepresentative[]>;
  selectedClient$ = new ReplaySubject<Client>();
  bankAccounts$: Observable<ClientBankAccount[]>;

  private selectedClient: Client | null = null;

  constructor(private applicationsService: ApplicationsService,
    private clientsService: ClientsService,
    private confirmationService: ConfirmationService,
    private countriesMapCache: CountriesMapCache,
    private router: Router,
    private builder: UntypedFormBuilder) {
    this.form = this.builder.group({
      name: [null, Validators.required],
      country: ['FI', Validators.required],
      business_id: [null, [Validators.required, clientBusinessIdValidator()]],
      iban: [null, Validators.required],
      firstName: [null, Validators.required],
      lastName: [null, Validators.required],
      socialSecurityNumber: [null],
      email: [null, [Validators.required, Validators.email]],
      phone: [null, Validators.required],
      clientRepresentative: [null],
    });

    this.form.get('country').valueChanges
      .subscribe(() => {
        const businessId = this.form.get('business_id');
        if (businessId) {
          businessId.markAsTouched();
          businessId.updateValueAndValidity();
        }
      });
  }

  clientSelected(client: Client): void {
    this.selectedClient = client;
    this.form.patchValue({
      name: client.name,
      country: client.country,
      business_id: client.business_id,
      iban: client.bank_accounts?.find(a => !!a.verified_at)?.iban ?? null,
      firstName: null,
      lastName: null,
      socialSecurityNumber: null,
      email: null,
      phone: null,
      clientRepresentative: null,
    });

    this.clientCtrl.setValue(client);
    this.selectedClient$.next(client);
  }

  displayFn(client: Client): string {
    return client && client.name ? client.name : '';
  }

  clientRepresentativeSelected(event: MatSelectChange): void {
    console.log(event);
    const clientRepresentative: ClientRepresentative = event.value;
    if (!clientRepresentative) {
      // AI: 'New' was selected, clear representative fields for manual entry.
      this.form.patchValue({
        firstName: null,
        lastName: null,
        socialSecurityNumber: null,
        email: null,
        phone: null,
      });
      return;
    }

    this.form.patchValue({
      firstName: clientRepresentative.first_name,
      lastName: clientRepresentative.last_name,
      // AI: The API's ClientRepresentative model does not include SSN.
      // socialSecurityNumber: clientRepresentative.ssn,
      email: clientRepresentative.email,
      phone: clientRepresentative.phone
    });
    console.log("form values", this.form.value);
  }

  save(): void {
    if (!this.form.valid) {
      return;
    }

    // Canonicalize the operator-entered business id after the validity guard, so
    // an aborted save no longer visibly rewrites the input (design D10, F8).
    // Kept before reading formValue so both the divergence check below
    // (buildUnverifiedIbanWarning) and the submitted payload see the stored
    // value. A client picked from search leaves the control pristine, so the
    // helper skips it and its business_id round-trips byte-unchanged.
    canonicalizeBusinessIdControl(this.form.get('business_id'));

    const formValue = this.form.value;

    if (this.uploadFiles.length === 0) {
      // AI: Here we could show an error to the user that at least one file is required.
      console.error('No files uploaded.');
      return;
    }

    const warning = this.buildUnverifiedIbanWarning(formValue);
    if (warning) {
      this.confirmationService.confirm(warning)
        .pipe(takeUntil(this.ngDestroy$), filter(confirmed => confirmed))
        .subscribe(() => this.submitApplication(formValue));
      return;
    }
    this.submitApplication(formValue);
  }

  // Returns the confirmation to show before submitting, or null when no
  // warning applies. Warns only for a client selected from the search
  // whose business_id still matches the form (a diverged business_id
  // means the operator has retyped the form for a different client).
  // The backend FinancingPaymentVerifier remains the hard gate; this is
  // an entry-time heads-up.
  private buildUnverifiedIbanWarning(formValue: typeof this.form.value): ConfirmationDialogModel | null {
    const client = this.selectedClient;
    if (!client || formValue.business_id !== client.business_id) {
      return null;
    }
    const verified = client.bank_accounts?.find(a => !!a.verified_at);
    const entered = toElectronicIban(formValue.iban) ?? '';
    if (!verified) {
      return new ConfirmationDialogModel(
        'No ownership-confirmed bank account',
        `${client.name} has no bank account with ownership confirmed. Financing will be blocked until a bank account's ownership is confirmed. Save anyway?`,
        'Save anyway',
        'Cancel',
        [
          { label: 'Client', value: client.name },
          { label: 'Entered IBAN', value: ibantools.friendlyFormatIBAN(entered) },
        ],
      );
    }
    if (toElectronicIban(verified.iban) === entered) {
      return null;
    }
    return new ConfirmationDialogModel(
      'Bank account ownership not confirmed',
      'The entered IBAN is not the client\'s ownership-confirmed bank account. Financing will be blocked until the invoice IBAN matches that account. Save anyway?',
      'Save anyway',
      'Cancel',
      [
        { label: 'Client', value: client.name },
        { label: 'Entered IBAN', value: ibantools.friendlyFormatIBAN(entered) },
        { label: 'Ownership-confirmed IBAN', value: ibantools.friendlyFormatIBAN(verified.iban) },
      ],
    );
  }

  private submitApplication(formValue: typeof this.form.value): void {
    // AI: This page only supports creating applications for CompanyClients.
    const metadata: CreateApplicationMetadata = {
      client_type: ClientType.Company,
      name: formValue.name,
      country: formValue.country,
      business_id: formValue.business_id,
      iban: formValue.iban,
      representative: {
        first_name: formValue.firstName,
        last_name: formValue.lastName,
        email: formValue.email,
        phone: formValue.phone,
        ssn: formValue.socialSecurityNumber
      }
    };

    const invoicePdf = this.uploadFiles[0];
    const otherAttachments = this.uploadFiles.length > 1 ? this.uploadFiles.slice(1) : undefined;

    this.applicationsService.createApplication({
      body: {
        invoice_pdf: invoicePdf,
        other_attachments: otherAttachments,
        metadata: metadata
      }
    }).pipe(takeUntil(this.ngDestroy$)).subscribe(() => {
      // The new API is async and returns an application ID.
      // We can't navigate directly to the invoice, so we go to the invoices list.
      // A success notification might be a good addition here in the future.
      this.router.navigateByUrl('/invoices');
    });
  }

  uploadFileAdded(file: File): void {
    this.uploadFiles.push(file);
  }

  removeUploadFile(file: File): void {
    this.uploadFiles = this.uploadFiles.filter(f => f !== file);
  }

  ngOnInit(): void {
    this.countries$ = this.countriesMapCache.map$;

    this.clients$ = this.clientCtrl.valueChanges
      .pipe(
        startWith(''),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(value => {
          // AI: This page only supports CompanyClient.
          const name = typeof value === 'string' ? value : (value as Client)?.name;
          if (!name || name.length < 2) {
            return of([]);
          }
          return this.clientsService.listClients({ q: name, client_types: [ClientType.Company] })
            .pipe(
              map(response => response.items)
            );
        })
      );

    this.clientRepresentatives$ = this.selectedClient$
      .pipe(
        takeUntil(this.ngDestroy$),
        switchMap(client => {
          // AI: This will need to be adapted for different client types.
          return this.clientsService.getClientRepresentatives({ client_id: client.id });
        }
        ),
        share(),
      );

    this.bankAccounts$ = this.selectedClient$.pipe(
      map(client => [...(client?.bank_accounts ?? [])].sort(compareBankAccounts)),
      share(),
    );
  }

  ngOnDestroy() {
    this.ngDestroy$.next(undefined);
    this.ngDestroy$.complete();
  }
}
